import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Paper
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  ScatterChart,
  Scatter,
  Legend
} from 'recharts';
import { analyticsService } from '../services/analyticsService';

const InventoryAnalytics = ({ filters = {} }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [valueByStatusData, setValueByStatusData] = useState([]);
  const [leadTimeData, setLeadTimeData] = useState([]);
  const [preparationScatterData, setPreparationScatterData] = useState([]);
  const [granularity, setGranularity] = useState('week');

  useEffect(() => {
    loadAnalytics();
  }, [filters, granularity]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const [valueByStatus, leadTime, preparationScatter] = await Promise.all([
        analyticsService.getInventoryValueByStatus(filters),
        analyticsService.getInventoryLeadTime({ ...filters, granularity }),
        analyticsService.getInventoryPreparationScatter({ 
          ...filters, 
          limit: 100,
          includeDelivered: false 
        })
      ]);

      console.log('Analytics Debug:', {
        valueByStatus,
        leadTime,
        preparationScatter
      });

      // The API returns data directly, not wrapped in a data property
      setValueByStatusData(Array.isArray(valueByStatus) ? valueByStatus : (valueByStatus.data || []));
      setLeadTimeData(Array.isArray(leadTime) ? leadTime : (leadTime.data || []));
      setPreparationScatterData(Array.isArray(preparationScatter) ? preparationScatter : (preparationScatter.data || []));

    } catch (err) {
      console.error('Erro ao carregar analytics de inventário:', err);
      setError(err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatStatusLabel = (status) => {
    const labels = {
      'em_aquisicao': 'Em Aquisição',
      'em_preparacao': 'Em Preparação',
      'pronto_sorteio': 'Pronto para Sorteio',
      'sorteado_aguardando': 'Sorteado (Aguardando)',
      'entregue': 'Entregue',
      'cancelado': 'Cancelado'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Grid container spacing={3}>
      {/* Valor por Status */}
      <Grid item xs={12} lg={6}>
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Valor por Status (não-entregues)
            </Typography>
            <Box sx={{ height: 300, mt: 2 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={valueByStatusData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="status" 
                    tickFormatter={formatStatusLabel}
                    angle={-45}
                    textAnchor="end"
                    height={100}
                  />
                  <YAxis tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`} />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'total_value' ? formatCurrency(value) : value,
                      name === 'total_value' ? 'Valor Total' : 'Quantidade'
                    ]}
                    labelFormatter={(label) => formatStatusLabel(label)}
                  />
                  <Bar dataKey="total_value" fill="#1976d2" name="Valor Total" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Lead Time por Status */}
      <Grid item xs={12} lg={6}>
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Lead Time por Status
              </Typography>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Granularidade</InputLabel>
                <Select
                  value={granularity}
                  onChange={(e) => setGranularity(e.target.value)}
                  label="Granularidade"
                >
                  <MenuItem value="day">Dia</MenuItem>
                  <MenuItem value="week">Semana</MenuItem>
                  <MenuItem value="month">Mês</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={leadTimeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="period" 
                    tickFormatter={(value) => new Date(value).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                  />
                  <YAxis tickFormatter={(value) => `${value.toFixed(0)}h`} />
                  <Tooltip 
                    formatter={(value, name) => [`${value.toFixed(1)} horas`, formatStatusLabel(name)]}
                    labelFormatter={(label) => new Date(label).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                  />
                  <Legend formatter={(value) => formatStatusLabel(value)} />
                  {Array.from(new Set(leadTimeData.map(d => d.status))).map((status, index) => (
                    <Line 
                      key={status}
                      type="monotone"
                      dataKey="avg_hours"
                      data={leadTimeData.filter(d => d.status === status)}
                      stroke={['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#d32f2f'][index]}
                      name={status}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Scatter: Dias em Preparação vs Valor Final */}
      <Grid item xs={12}>
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Dias em Preparação vs Valor Final
            </Typography>
            <Box sx={{ height: 400, mt: 2 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart data={preparationScatterData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="days_in_preparation" 
                    name="Dias em Preparação"
                    tickFormatter={(value) => `${value.toFixed(0)}d`}
                  />
                  <YAxis 
                    dataKey="final_value" 
                    name="Valor Final"
                    tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'final_value' ? formatCurrency(value) : `${value.toFixed(1)} dias`,
                      name === 'final_value' ? 'Valor Final' : 'Dias em Preparação'
                    ]}
                    labelFormatter={() => ''}
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <Paper sx={{ p: 2 }}>
                            <Typography variant="subtitle2">{data.name}</Typography>
                            <Typography variant="body2">
                              Categoria: {data.category}
                            </Typography>
                            <Typography variant="body2">
                              Dias em Preparação: {data.days_in_preparation?.toFixed(1)}
                            </Typography>
                            <Typography variant="body2">
                              Valor Final: {formatCurrency(data.final_value)}
                            </Typography>
                          </Paper>
                        );
                      }
                      return null;
                    }}
                  />
                  <Scatter dataKey="final_value" fill="#1976d2" />
                </ScatterChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default InventoryAnalytics;